/**
 * Dynamic Stripe Checkout Sessions integration
 */

export interface StripeCheckoutOptions {
  priceId: string;
  campId?: string;
  campSlug?: string;
  planName?: string;
  userEmail?: string;
}

/**
 * Create and redirect to Stripe Checkout Session
 * Uses dynamic URLs based on current origin
 */
export const redirectToStripeCheckout = async (options: StripeCheckoutOptions) => {
  const {
    priceId,
    campId,
    campSlug,
    planName = 'trailhead',
    userEmail,
  } = options;

  try {
    // Call our Supabase Edge Function to create a checkout session
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        priceId,
        campId,
        campSlug,
        planName,
        userEmail,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      // Provide more user-friendly error messages
      if (error.code === 'PRICE_NOT_FOUND' || error.details?.includes('does not exist')) {
        throw new Error('Payment system configuration error. Please contact support.');
      }
      throw new Error(error.details || error.error || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    
    if (!url) {
      throw new Error('No checkout URL returned');
    }

    // Store camp info in sessionStorage for the success page
    if (campSlug) {
      sessionStorage.setItem('pending_camp_slug', campSlug);
      sessionStorage.setItem('pending_plan_name', planName);
    }
    
    // Set flag to detect if user returns without completing
    sessionStorage.setItem('stripe_checkout_initiated', 'true');

    // Redirect to Stripe checkout
    window.location.href = url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    // Return error to caller instead of showing alert
    throw error;
  }
};

/**
 * Get the Trailhead Price ID from environment
 * This should match your Stripe product price ID
 */
export const getTrailheadPriceId = (): string => {
  // Use DEV price ID for development, PROD for production
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_TRAILHEAD_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_TRAILHEAD_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_TRAILHEAD_PRICE_ID || '';
};

/**
 * Get the Campfire Price ID from environment
 * This should match your Stripe product price ID
 */
export const getCampfirePriceId = (): string => {
  // Use DEV price ID for development, PROD for production
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_CAMPFIRE_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_CAMPFIRE_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_CAMPFIRE_PRICE_ID || '';
};

/**
 * Get the Summit Price ID from environment
 * This should match your Stripe product price ID
 */
export const getSummitPriceId = (): string => {
  // Use DEV price ID for development, PROD for production
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_SUMMIT_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_SUMMIT_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_SUMMIT_PRICE_ID || '';
};

/**
 * Get the Everest Price ID from environment
 * This should match your Stripe product price ID
 */
export const getEverestPriceId = (): string => {
  // Use DEV price ID for development, PROD for production
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_EVEREST_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_EVEREST_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_EVEREST_PRICE_ID || '';
};

/**
 * Get price ID for a specific plan and billing cycle
 */
export const getPriceIdForPlan = (planId: string, billingCycle: 'monthly' | 'annual' = 'annual'): string => {
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  
  switch (planId) {
    case 'trailhead':
      if (billingCycle === 'monthly') {
        return isDev
          ? import.meta.env.VITE_STRIPE_TRAILHEAD_MONTHLY_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_TRAILHEAD_MONTHLY_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_TRAILHEAD_MONTHLY_PRICE_ID || '';
      } else {
        return isDev
          ? import.meta.env.VITE_STRIPE_TRAILHEAD_ANNUAL_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_TRAILHEAD_ANNUAL_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_TRAILHEAD_ANNUAL_PRICE_ID || '';
      }
    case 'campfire':
      if (billingCycle === 'monthly') {
        return isDev
          ? import.meta.env.VITE_STRIPE_CAMPFIRE_MONTHLY_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_CAMPFIRE_MONTHLY_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_CAMPFIRE_MONTHLY_PRICE_ID || '';
      } else {
        return isDev
          ? import.meta.env.VITE_STRIPE_CAMPFIRE_ANNUAL_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_CAMPFIRE_ANNUAL_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_CAMPFIRE_ANNUAL_PRICE_ID || '';
      }
    case 'summit':
      if (billingCycle === 'monthly') {
        return isDev
          ? import.meta.env.VITE_STRIPE_SUMMIT_MONTHLY_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_SUMMIT_MONTHLY_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_SUMMIT_MONTHLY_PRICE_ID || '';
      } else {
        return isDev
          ? import.meta.env.VITE_STRIPE_SUMMIT_ANNUAL_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_SUMMIT_ANNUAL_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_SUMMIT_ANNUAL_PRICE_ID || '';
      }
    case 'everest':
      if (billingCycle === 'monthly') {
        return isDev
          ? import.meta.env.VITE_STRIPE_EVEREST_MONTHLY_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_EVEREST_MONTHLY_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_EVEREST_MONTHLY_PRICE_ID || '';
      } else {
        return isDev
          ? import.meta.env.VITE_STRIPE_EVEREST_ANNUAL_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_EVEREST_ANNUAL_PRICE_ID || ''
          : import.meta.env.VITE_STRIPE_EVEREST_ANNUAL_PRICE_ID || '';
      }
    default:
      return '';
  }
};

/**
 * Check if Stripe is configured
 */
export const isStripeConfigured = (): boolean => {
  return !!getTrailheadPriceId();
};

/**
 * Check if a specific plan is configured for a billing cycle
 */
export const isPlanConfigured = (planId: string, billingCycle: 'monthly' | 'annual' = 'annual'): boolean => {
  return !!getPriceIdForPlan(planId, billingCycle);
};

/**
 * Get the Parents LTD Price ID from environment
 * This should match your Stripe product price ID for $79 one-time payment
 */
export const getLTDParentsPriceId = (): string => {
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  const devPriceId = import.meta.env.VITE_STRIPE_LTD_PARENTS_PRICE_ID_DEV;
  const prodPriceId = import.meta.env.VITE_STRIPE_LTD_PARENTS_PRICE_ID;
  
  // Debug logging (only in development)
  if (isDev) {
    console.log('[getLTDParentsPriceId] Environment check:', {
      isDev,
      nodeEnv: import.meta.env.NODE_ENV,
      devPriceIdExists: devPriceId !== undefined,
      devPriceIdValue: devPriceId ? `${devPriceId.substring(0, 10)}...` : '(empty)',
      prodPriceIdExists: prodPriceId !== undefined,
      prodPriceIdValue: prodPriceId ? `${prodPriceId.substring(0, 10)}...` : '(empty)',
    });
  }
  
  const priceId = isDev 
    ? devPriceId || prodPriceId || ''
    : prodPriceId || '';
  
  if (!priceId && isDev) {
    console.warn('[getLTDParentsPriceId] Price ID not found. Check your .env file:', {
      expectedVarName: 'VITE_STRIPE_LTD_PARENTS_PRICE_ID_DEV',
      envVarExists: devPriceId !== undefined,
      envVarValue: devPriceId || '(empty or undefined)',
      tip: 'Make sure to restart the dev server after adding/updating .env variables',
    });
  }
  
  return priceId;
};

/**
 * Get the Trailhead LTD Price ID from environment
 * This should match your Stripe product price ID for $149 one-time payment
 */
export const getLTDTrailheadPriceId = (): string => {
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_LTD_TRAILHEAD_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_LTD_TRAILHEAD_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_LTD_TRAILHEAD_PRICE_ID || '';
};

/**
 * Get the Campfire LTD Price ID from environment
 * This should match your Stripe product price ID for $249 one-time payment
 */
export const getLTDCampfirePriceId = (): string => {
  const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  return isDev 
    ? import.meta.env.VITE_STRIPE_LTD_CAMPFIRE_PRICE_ID_DEV || import.meta.env.VITE_STRIPE_LTD_CAMPFIRE_PRICE_ID || ''
    : import.meta.env.VITE_STRIPE_LTD_CAMPFIRE_PRICE_ID || '';
};

/**
 * Check if an LTD plan is configured
 */
export const isLTDPlanConfigured = (planId: 'ltd-parents' | 'ltd-trailhead' | 'ltd-campfire'): boolean => {
  switch (planId) {
    case 'ltd-parents':
      return !!getLTDParentsPriceId();
    case 'ltd-trailhead':
      return !!getLTDTrailheadPriceId();
    case 'ltd-campfire':
      return !!getLTDCampfirePriceId();
    default:
      return false;
  }
};